import { RatingType } from '../../reviews/models/reviews.model';

export enum endpoint {
  getTemplate = 'templates',
  createAnswer = 'answers/create',
  sendDeclaration = 'user-reviewer-form/send-declaration',
  answer = 'answers',
  getForm = 'forms',
  formsValidateLimitDate = 'forms/formsValidateLimitDate',
  dropdownAssigmentsByUser = 'forms/activeForms',
  updateAnswer = 'answers/update',
  deleteAnswer = 'answers/delete',
  updateStatusForm = 'forms/update-state-form',
  getAdvanceForm = 'forms/advanceForm',
  bulkAnswerQuestionTable = 'answers/create-many',
  bulkAnswerUpdateQuestionTable = 'answers/update-answers-table',
  bulkAnswerQuestionTableUpdate = 'answers/update-many',
  uploadFile = 'azure/upload-many',
  getDocument = 'questions/documents',
  uploadFilesQuestion = 'questions/upload-files',
  deleteFile = 'azure/delete',
  getRowTable = 'questions-table/getRowTable',
  deleteRowTable = 'answers/delete-answers-table',
  createRowTable = 'answers/rows',
  updateAnswersTableRow = 'answers/update-answer-table',
  updateTable = 'questions-table/questions-and-answers',
  requestExtension = 'extensions/files-upload',
  getCertificates = 'certifications/certifications-by-id',
}

export interface CreateAnswerBody {
  answers_id?: number;
  answers_question_id: number;
  answer_form_id: number;
  sectionPosition?: number;
  answers_value: string | string[] | null;
  answers_format: string;
  answers_type: string;
  answers_validation: boolean;
  /** ID del formulario (siempre enviado en create/update) */
  idForm?: number;
}

export interface CreateAnswerWithOtherBody extends CreateAnswerBody {
  answer_allow_other: boolean;
  answer_allow_other_value: string | null;
}

export interface UpdateAnswerBody {
  sectionPosition?: number;
  answers_value: string | string[] | null;
  /** ID del formulario (siempre enviado en update) */
  idForm?: number;
}

export interface UpdateAnswerWithOtherBody extends UpdateAnswerBody {
  answer_allow_other: boolean;
  answer_allow_other_value: string | null;
}

export interface DropdownAssigmentsByUserResponse {
  data: Data[];
  message: string;
  code: number;
}

/** Item de visibilidad condicional que puede venir en el response de create/update answer. */
export interface ConditionalVisibilityItem {
  question_table?: number;
  question?: number;
  element_type: 'question_table' | 'question';
  show: boolean;
}

export interface CreateAnswerResponse {
  data: DataAnswer;
  message: string;
  code: number;
}

export interface DataAnswer {
  answerss: Answer;
  list?: ConditionalVisibilityItem[];
}

export interface UpdateAnswerResponse {
  data: Answer & { list?: ConditionalVisibilityItem[] };
  message: string;
  code: number;
}

export interface Data {
  form_id: number;
  form_name: string;
}
// 📘 Interfaces base

export interface GetFormResponse {
  code: string;
  data: FormData;
  message: string;
}

export interface FormsValidateLimitDateResponse {
  data: {
    valid: boolean;
    message?: string;
    daysPassed: number;
    daysLeft: number;
  };
  message: string;
  code: number;
}

// -------------------- FORM --------------------

export interface FormData {
  form_id: number;
  form_template_id: number;
  form_name: string;
  form_limit_date: string;
  form_start_date: string;
  form_send_date: string | null;
  form_status_id: number;
  form_review_status_id: number;
  form_all_update_date: string;
  form_is_deleted: boolean;
  form_created_by: number;
  form_created_date: string;
  form_update_by: number;
  form_update_date: string;
  form_copy_status: string | null;
  status_declaration: GeneralStatus;
  status_review: GeneralStatus;
  template: Template;
  reviewer: ReviewerData;
  certification_url?: string | null;
  IdDeclarant?: number;
  declarantName: string;
}
// -------------------- TEMPLATE --------------------
export interface Template {
  template_id: number;
  template_title: string;
  template_description: string;
  template_status_id: number;
  template_is_deleted: boolean;
  template_all_update_date: string;
  template_created_by: number;
  template_created_date: string;
  template_update_by: number;
  template_update_date: string;
  totalSections: number;
  generalStatus: GeneralStatus;
  sections: TemplateSection[];
}

// -------------------- SECTION --------------------

export interface TemplateSection {
  template_section_id: number;
  template_section_template_id: number;
  template_section_name: string;
  template_section_position: number;
  template_section_description: string;
  template_section_is_deleted: boolean;
  template_section_created_date: string;
  template_section_created_by: number;
  elements: SectionElement[];
}

// -------------------- ELEMENTS --------------------

/** Condición de visibilidad para una pregunta (cuando question_has_conditions es true). */
export interface QuestionCondition {
  question_condition_id: number;
  question_condition_question_id: number;
  question_condition_trigger_question_id: number;
  question_condition_operator_type_id: number;
  question_condition_value_primary: string;
  question_condition_value_secundary: string;
  question_condition_is_deleted: boolean;
  question_condition_created_by: number;
  question_condition_created_date: string;
}

/** Condición de visibilidad para una tabla de preguntas (cuando questions_table_has_conditions es true). */
export interface QuestionTableCondition {
  question_condition_id: number;
  question_condition_question_table_id: number;
  question_condition_trigger_question_id: number;
  question_condition_operator_type_id: number;
  question_condition_value_primary: string;
  question_condition_value_secundary: string;
  question_condition_is_deleted: boolean;
  question_condition_created_by: number;
  question_condition_created_date: string;
}

export type SectionElement =
  | QuestionElement
  | QuestionTableElement
  | StaticElement
  | SubsectionElement;

// ---- Question Element ----
export interface QuestionElement {
  element_type: 'question';
  question_id: number;
  question_template_section_id: number;
  question_section_position: number;
  question_type_id: number;
  question_name: string;
  question_name_placeholder?: string;
  question_description?: string;
  question_options: string[];
  question_mandatory: boolean;
  question_duplicate: boolean;
  question_related_document?: boolean;
  countDocuments: number;
  countMessagesReviewer?: number;
  countMessagesAdmin?: number;
  countTotalMessages?: number;
  question_is_deleted: boolean;
  question_created_by: number;
  question_created_date: string;
  question_update_by?: number;
  question_update_date?: string;
  question_min_length?: number;
  question_max_length?: number;
  question_allow_zero?: boolean;
  question_validate_over_18?: boolean;
  question_validate_future_date?: boolean;
  question_validate_past_date?: boolean;
  question_allow_other?: boolean;
  question_document_required?: boolean;
  question_is_dropdown?: boolean;
  typeQuestion: GeneralType;
  answers: Answer[];
  conflictInteresTracking?: ConflictInteresTracking | null;
  /** Si existe, indica visibilidad según condiciones. Cuando no hay condicionales no viene. */
  show?: boolean;
  /** Indica que la pregunta tiene condiciones de visibilidad. */
  question_has_conditions?: boolean;
  /** Condiciones que determinan cuándo mostrar esta pregunta (solo si question_has_conditions es true). */
  conditions?: QuestionCondition[];
}

// ---- Static Element ----
export interface StaticElement {
  element_type: 'static_element';
  static_element_id: number;
  static_element_section_id: number;
  static_element_type_id: number;
  static_element_value: string;
  static_element_position: number;
  static_element_is_deleted: boolean;
  static_element_created_by: number;
  static_element_created_date: string;
  typeStatic: GeneralType;
}

// ---- Question Table Element ----
export interface QuestionTableElement {
  element_type: 'question_table';
  questions_table_id: number;
  questions_table_section_id: number;
  questions_table_section_position: number;
  questions_table_title: string;
  questions_table_description: string;
  questions_table_is_deleted: boolean;
  questions_table_created_by: number;
  questions_table_created_date: string;
  questions_table_update_by?: number;
  questions_table_update_date?: string;
  question_table_answers_rows_finished: number;
  questions_table_type_id: number;
  questions_table_has_conditions: boolean;
  typeTable: GeneralType;
  elementsTable: QuestionOfTable[];
  /** Condiciones que determinan cuándo mostrar esta tabla (solo si questions_table_has_conditions es true). */
  conditionsTable?: QuestionTableCondition[];
  /** Si existe, indica visibilidad según condiciones. Cuando no hay condicionales no viene. */
  show?: boolean;
}

// ---- Subsection Element ----
export interface SubsectionElement {
  element_type: 'subSection';
  template_subsection_id: number;
  template_subsection_template_id: number;
  template_subsection_section_id: number;
  template_subsection_collapse: boolean;
  template_subsection_contain: number[];
  template_subsection_name: string;
  template_subsection_is_deleted: boolean;
  template_subsection_created_by: number;
  template_subsection_created_date: string;
  template_subsection_updated_by?: number;
  template_subsection_updated_date?: string;
  elements: QuestionElement[];
}
export interface QuestionOfTable {
  question_id: number;
  question_table_id: number;
  question_table_position: number;
  question_template_section_id: number;
  question_type_id: number;
  question_name: string;
  question_name_placeholder?: string;
  question_description?: string;
  question_options: string[];
  question_section_position: number;
  question_mandatory: boolean;
  question_duplicate: boolean;
  question_related_document?: boolean;
  countDocuments: number;
  countMessagesReviewer?: number;
  countMessagesAdmin?: number;
  countTotalMessages?: number;
  question_is_deleted: boolean;
  question_created_by: number;
  question_created_date: string;
  question_update_by?: number;
  question_update_date?: string;
  question_min_length?: number;
  question_max_length?: number;
  question_allow_zero?: boolean;
  question_validate_over_18?: boolean;
  question_validate_future_date?: boolean;
  question_validate_past_date?: boolean;
  question_allow_other?: boolean;
  question_document_required?: boolean;
  question_is_dropdown?: boolean;
  typeQuestionOfTable: GeneralType;
  answers: Answer[];
  conflictInteresTracking?: ConflictInteresTracking | null;
}

export interface Answer {
  answers_id: number;
  answers_question_id: number;
  answer_form_id: number;
  answers_user_id: number;
  answers_value: string | null;
  answers_format: string;
  answers_type: string;
  answers_slug_question_type: string | null;
  answers_status_id: number | null;
  answers_validation: boolean;
  answers_is_deleted: boolean;
  answers_created_by: number;
  answers_created_date: string;
  answers_reviewer_by: number | null;
  answers_reviewer_date: string | null;
  answers_updated_by: number;
  answers_updated_date: string;
  answer_allow_other: boolean;
  answer_allow_other_value: string;
  answersTable?: AnswersTable[];
  answer_is_review_approved: boolean;

  // (vienen dentro de answers[] cuando es tabla)
  countMessagesReviewer?: number;
  countMessagesAdmin?: number;
  countTotalMessages?: number;
  countDocuments?: number;
  conflictInteresTracking?: ConflictInteresTracking | null;
}

export interface AnswersTable {
  answers_table_id: number;
  answers_table_answer_id: number;
  answers_table_question_table_id: number;
  answers_table_row_number: number;
  answers_table_is_deleted: boolean;
  answers_table_created_by: number;
  answers_table_created_date: string;
}

export interface GeneralStatus {
  general_status_id: number;
  general_status_type_id: number;
  general_status_name: string;
  general_status_value_name: string;
  general_status_description: string;
  general_status_is_deleted: boolean;
  general_status_create_by: number;
  general_status_create_date: string;
}

export interface GeneralType {
  general_type_id: number;
  general_type_group: string;
  general_type_description: string;
  general_type_value: string;
  general_type_is_deleted: boolean;
  general_type_create_by: number;
  general_type_create_date: string;
}

export interface GetAdvanceFormResponse {
  data: AdvanceData;
  message: string;
  code: number;
}
// interfaces.ts
export interface FormStatusData {
  section_id: number;
  section_name: string;
  section_description: string | null;
  status: string; // Cambiado de section_advance_status
}

export interface GeneralStatusAdvanceData {
  general_status_name: string;
  general_status_value_name: string;
}

export interface AdvanceData {
  advance: FormStatusData[];
  status: GeneralStatusAdvanceData;
  limit_date: string;
  assignment_copy_status?: string | null;
  form_copy_status?: string | null;
  form_copy_available?: boolean;
}

export interface GetRowTableResponse {
  data: GetRowTableData;
  message: string;
  code: number;
}

export interface UpdateQuestionTableResponse {
  data: QuestionTableElement;
  message: string;
  code: number;
}

interface GetRowTableData {
  questions_table_id: number;
  questions_table_title: string;
  question_id: number;
  question_description: string | null;
  answers_table_id: number;
  answers_table_row_number: number;
  answers_id: number;
  user_id: number;
  answers_value: string;
}

export interface CreateRowTableBody {
  idQuestionTable: number;
  idForm: number;
  rowNumber: number;
}
export interface CreateRowAnswersResponse {
  data: AnswerItem[];
  message: string;
  code: number;
}

export interface AnswerItem {
  answers_table_id: number;
  answers_table_created_by: number;
  answers_table_questions_id: number;
  answers_table_created_date: string;
  answers_table_question_table_id: number;
  answers_table_is_deleted: boolean;
  answers_table_row_number: number;
  answers_table_answer_id: number;
  answers_table_update_by: number | null;
  answers_table_update_date: string | null;
}

export interface UpdateAnswersTableRowBody {
  idQuestion: number;
  idForm: number;
  idAnswer: number;
  requestBody: CreateAnswerBody[];
}
export interface AnswerUpdateResponse {
  data: Answer;
  message: string;
  code: number;
}

export interface RequestExtensionPayload {
  idForm: number;
  extensionUserId: number;
  extensionReasonId: number;
  document_description: string;
  extensionDate: string;
  file: File[];
}

export interface ReviewerData {
  reviewer_id: number;
  reviewer_name: string;
  reviewer_lastname: string;
  reviewer_fullname: string | null;
}

// CONSTANCIAS
export interface Certification {
  certification_id: number;
  certification_user_id: number;
  certification_number: string;
  certification_form_id: number;
  certification_url: string;
  certification_created_by: number;
  certification_created_date: string;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  code: number;
}

export interface ConflictInteresTracking {
  conflict_interes_tracking_id: number;
  conflict_interes_tracking_answer_id?: number | null;
  conflict_interes_tracking_is_status_ok: boolean;

  // opcionales si existen
  conflict_interes_tracking_length_conflict?: number;
  conflict_interes_tracking_length_tracking?: number;

  conflict_interes_tracking_rating_type: RatingType;
}

export type CertificationsByUserResponse = ApiResponse<Certification[]>;
